import { useAppSelector } from '../store';
import { UserRole } from '../types';

interface PermissionHook {
  hasRole: (role: number) => boolean;
  /** Returns true if the user holds the named right (from RoleRights table) */
  hasRight: (rightName: string) => boolean;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  /** EmployerAdmin = company owner (create/edit company, post jobs, review) */
  isEmployerAdmin: () => boolean;
  /** Employer = staff (post jobs, review applicants — no company management) */
  isEmployer: () => boolean;
  /** True for both EmployerAdmin and Employer */
  isAnyEmployer: () => boolean;
  isJobSeeker: () => boolean;
  isStudent: () => boolean;
  /** True if the user can post, edit, or delete job listings */
  canManageJobs: () => boolean;
  /** True if the user can create or edit the company profile */
  canManageCompany: () => boolean;
  /** True for job-seeking roles (JobSeeker + Student) */
  canApply: () => boolean;
  canManageUsers: () => boolean;
  canAccessAdmin: () => boolean;
  /** True if the user's profile should include a resume section */
  showResume: () => boolean;
  // ── New right-based checks ────────────────────────────────────────────────
  /** Can browse/search the applicant list */
  canViewApplicants: () => boolean;
  /** Can open an individual applicant's full profile */
  canViewApplicantProfile: () => boolean;
  /** Can send a job-opportunity email to an applicant */
  canSendJobOpportunity: () => boolean;
  /** Can view a company profile detail page */
  canViewCompanyProfile: () => boolean;
}

/** Map string role names (sent by backend) to numeric UserRole values */
const ROLE_NAME_MAP: Record<string, number> = {
  SuperAdmin: UserRole.SuperAdmin,
  Admin: UserRole.Admin,
  EmployerAdmin: UserRole.EmployerAdmin,
  JobSeeker: UserRole.JobSeeker,
  Student: UserRole.Student,
  Employer: UserRole.Employer,
};

/** Normalise a role value that may arrive as a string ("SuperAdmin") or number (0) */
function normaliseRole(role: unknown): number {
  if (typeof role === 'number') return role;
  if (typeof role === 'string' && role in ROLE_NAME_MAP) return ROLE_NAME_MAP[role];
  return -1; // unknown
}

export function usePermission(): PermissionHook {
  const user = useAppSelector((state) => state.auth.user);
  const rights = useAppSelector((state) => state.auth.rights);

  const hasRole = (role: number): boolean => normaliseRole(user?.role) === role;

  /**
   * Checks whether the current user has the named right.
   * Falls back to role-based logic when rights haven't been loaded yet
   * (e.g. on first page-load before the my-rights call completes).
   */
  const hasRight = (rightName: string): boolean => {
    if (rights.length > 0) return rights.includes(rightName);
    // Fallback: admins have everything, employers have job-related rights
    if (!user) return false;
    if (user.role === UserRole.SuperAdmin || user.role === UserRole.Admin) return true;
    return false;
  };

  const isAdmin = (): boolean => {
    const r = normaliseRole(user?.role);
    return r === UserRole.Admin || r === UserRole.SuperAdmin;
  };

  const isSuperAdmin = (): boolean => normaliseRole(user?.role) === UserRole.SuperAdmin;

  const isEmployerAdmin = (): boolean => normaliseRole(user?.role) === UserRole.EmployerAdmin;

  const isEmployer = (): boolean => normaliseRole(user?.role) === UserRole.Employer;

  const isAnyEmployer = (): boolean => isEmployerAdmin() || isEmployer();

  const isJobSeeker = (): boolean => normaliseRole(user?.role) === UserRole.JobSeeker;

  const isStudent = (): boolean => normaliseRole(user?.role) === UserRole.Student;

  /** Both employer types and admins can post / manage jobs */
  const canManageJobs = (): boolean => isAnyEmployer() || isAdmin();

  /** Only EmployerAdmin (company owner) can create/edit the company profile */
  const canManageCompany = (): boolean => isEmployerAdmin();

  /** JobSeekers and Students can apply */
  const canApply = (): boolean => isJobSeeker() || isStudent();

  const canManageUsers = (): boolean => isAdmin();

  const canAccessAdmin = (): boolean => isAdmin();

  /** Resume upload is only relevant for job-seeking roles */
  const showResume = (): boolean => isJobSeeker() || isStudent();

  // ── Right-based checks (with fallback to role for safety) ─────────────────

  const canViewApplicants = (): boolean => {
    // Job seekers and students must NEVER see the applicants list
    if (isJobSeeker() || isStudent()) return false;
    return hasRight('ViewApplicants') || isAdmin() || isAnyEmployer();
  };

  const canViewApplicantProfile = (): boolean =>
    hasRight('ViewApplicantProfile') || isAdmin() || isAnyEmployer();

  const canSendJobOpportunity = (): boolean =>
    hasRight('SendJobOpportunity') || isAdmin() || isAnyEmployer();

  const canViewCompanyProfile = (): boolean =>
    // Everyone (including guests) can see company profiles; rights just guard the route when logged in
    !user ? true : hasRight('ViewCompanyProfile') || true;

  return {
    hasRole,
    hasRight,
    isAdmin,
    isSuperAdmin,
    isEmployerAdmin,
    isEmployer,
    isAnyEmployer,
    isJobSeeker,
    isStudent,
    canManageJobs,
    canManageCompany,
    canApply,
    canManageUsers,
    canAccessAdmin,
    showResume,
    canViewApplicants,
    canViewApplicantProfile,
    canSendJobOpportunity,
    canViewCompanyProfile,
  };
}
